import {
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import Keycloak, {
  KeycloakConfig,
  KeycloakInitOptions,
  KeycloakLoginOptions,
  KeycloakLogoutOptions,
  KeycloakProfile,
} from 'keycloak-js';
import {
  NavigationGuardNext,
  RouteLocationNormalized, Router,
} from 'vue-router';

export const KEYCLOAK_INIT_OPTIONS: KeycloakInitOptions = {
  checkLoginIframe: false,
  flow: 'standard',
  onLoad: 'login-required',
  pkceMethod: 'S256',
  responseMode: 'query',
};

const LOCAL_KEY = 'keycloak_tokens';

type TTokenStore = Required<Pick<KeycloakInitOptions, 'token' | 'idToken' | 'refreshToken'>>;

/**
 * Loads the saved tokens from localStorage.
 *
 * @returns {TTokenStore | undefined} The parsed token store object if found, otherwise `undefined`.
 */
function loadTokens(): TTokenStore | undefined {
  const saved = localStorage.getItem(LOCAL_KEY);
  return saved ? JSON.parse(saved) as TTokenStore : undefined;
}

/**
 * Removes the stored tokens from localStorage.
 *
 * Useful for logging out or clearing authentication state.
 */
function removeTokens() {
  localStorage.removeItem(LOCAL_KEY);
}

/**
 * Saves the current tokens from the Keycloak instance to localStorage,
 * but only if the user is authenticated.
 *
 * @param {Keycloak} keycloak - The Keycloak instance containing authentication tokens.
 *
 * @remarks
 * This function checks `keycloak.authenticated` before attempting to store tokens.
 * It assumes that `token`, `refreshToken`, and `idToken` are defined when authenticated.
 */
function storeTokens(keycloak: Keycloak) {
  if (keycloak.authenticated) {
    localStorage.setItem(
      LOCAL_KEY,
      JSON.stringify({
        token: keycloak.token!,
        refreshToken: keycloak.refreshToken!,
        idToken: keycloak.idToken!,
      }),
    );
  }
}

export const MIN_TOKEN_VALIDITY_SECONDS = 60;
export const DEFAULT_REDIRECT_QUERY_PARAM = 'backUrl';
/**
 * Configuration options for initializing KeycloakAuthentication.
 */
export type TKeycloakAuthenticationOptions = {
  /**
   * Keycloak JS configuration object.
   * This defines the realm, clientId, and server URL.
   */
  keycloakConfig: KeycloakConfig,

  /**
   * Optional configuration for handling redirection after login.
   * If defined, the application will redirect to a dedicated login handler route
   * that can process the Keycloak response and then redirect back to the original page.
   */
  loginHandler?: {
    /**
     * The relative route path (e.g. "/login-redirect") that will handle the login
     * response.
     */
    path: string,

    /**
     * Name of the query parameter used to store the original page URL for redirection.
     * Defaults to `backUrl` if not provided.
     */
    redirectQueryParam?: string,
  },
};

export class KeycloakAuthentication {
  public keycloak: Keycloak;
  public config: TKeycloakAuthenticationOptions;

  constructor(options: TKeycloakAuthenticationOptions) {
    this.keycloak = new Keycloak(options.keycloakConfig);
    this.config = options;

    if (this.config.loginHandler && this.config.loginHandler.redirectQueryParam === undefined) {
      this.config.loginHandler.redirectQueryParam = DEFAULT_REDIRECT_QUERY_PARAM;
    }

    this.handleInterceptorFulfilled = this.handleInterceptorFulfilled.bind(this);
    this.handleInterceptorReject = this.handleInterceptorReject.bind(this);
    this.getRedirectUri = this.getRedirectUri.bind(this);
    this.resolveBackUrl = this.resolveBackUrl.bind(this);
  }

  /**
   * Indicates whether the user is currently authenticated.
   */
  get authenticated(): boolean {
    return this.keycloak.authenticated || false;
  }

  /**
   * Loads the user's profile from the Keycloak server.
   * Returns `undefined` if not authenticated.
   */
  async getUserProfile(): Promise<KeycloakProfile | undefined> {
    if (!this.authenticated) {
      return undefined;
    }
    return this.keycloak.loadUserProfile();
  }

  /**
   * Initializes the Keycloak instance. The `redirectUri` is built by
   * `getRedirectUri()` from the current location, so a `loginHandler` route
   * receives the original page as its `backUrl` query and the user returns
   * there after authenticating.
   *
   * Should be called once before app mount (usually in `main.ts`).
   */
  initialize(): Promise<boolean> {
    if (this.keycloak.didInitialize) {
      return Promise.resolve(this.keycloak.authenticated || false);
    }

    this.keycloak.onAuthSuccess = () => storeTokens(this.keycloak);
    this.keycloak.onAuthRefreshSuccess = () => storeTokens(this.keycloak);
    this.keycloak.onAuthLogout = removeTokens;

    const redirectUri = this.getRedirectUri();

    return this.keycloak.init({
      ...KEYCLOAK_INIT_OPTIONS,
      ...loadTokens(),
      redirectUri,
    });
  }

  /**
   * Triggers the Keycloak login flow.
   * Optionally accepts Keycloak login options like `redirectUri`.
   */
  login(options?: KeycloakLoginOptions): Promise<void> {
    return this.keycloak.login(options);
  }

  /**
   * Triggers the Keycloak logout flow.
   */
  logout(options?: KeycloakLogoutOptions) {
    removeTokens();
    return this.keycloak.logout(options);
  }

  getRedirectUri(fullPathOverwrite?: string): string {
    const {
      origin,
      pathname,
      search,
      hash,
    } = window.location;
    const rawPath = fullPathOverwrite ?? `${pathname}${search}${hash}`;
    const backUrl = this.resolveBackUrl(rawPath);

    if (this.config.loginHandler) {
      const queryParam = this.config.loginHandler.redirectQueryParam || DEFAULT_REDIRECT_QUERY_PARAM;
      return `${origin}${this.config.loginHandler.path}?${queryParam}=${encodeURIComponent(backUrl)}`;
    } else {
      return `${origin}${backUrl}`;
    }
  }

  /**
   * Figures out where to send the user after login, given a candidate URL or path.
   *
   * The candidate might already point back at the login route itself — say, after
   * Keycloak tacked on its own `code`/`state`/`iss` params, or after a few redirects
   * piled up. We keep reading the `backUrl` query and unwrapping it, which flattens
   * nested `login?backUrl=login?backUrl=...` chains and quietly discards the Keycloak
   * callback params (only `backUrl` survives each hop). If nothing useful is left,
   * fall back to the app root.
   */
  resolveBackUrl(candidate: string): string {
    const {
      origin,
    } = window.location;
    const loginPath = this.config.loginHandler?.path;
    const queryParam = this.config.loginHandler?.redirectQueryParam || DEFAULT_REDIRECT_QUERY_PARAM;

    let target = new URL(candidate, origin);
    while (
      loginPath !== undefined
      && target.pathname === loginPath
      && target.searchParams.has(queryParam)
    ) {
      target = new URL(target.searchParams.get(queryParam)!, origin);
    }

    if (loginPath !== undefined && target.pathname === loginPath) {
      return '/';
    }

    return `${target.pathname}${target.search}${target.hash}`;
  }

  /**
   * Adds a global Vue Router guard that checks for authentication
   * before each route change. If not authenticated, it redirects to Keycloak login.
   */
  addRouterMiddleware(router: Router): void {
    router.beforeEach(
      async (
        to: RouteLocationNormalized,
        _from: RouteLocationNormalized,
        next: NavigationGuardNext,
      ): Promise<void> => {
        if (this.authenticated) {
          next();
          return;
        }
        await this.login({
          redirectUri: this.getRedirectUri(to.fullPath),
        });
      },
    );
  }

  async handleInterceptorFulfilled(
    axiosRequestConfig: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> {
    if (!this.authenticated) {
      await this.login({
        redirectUri: this.getRedirectUri(),
      });
    } else {
      try {
        await this.keycloak.updateToken(MIN_TOKEN_VALIDITY_SECONDS);
      } catch {
        await this.login({
          redirectUri: this.getRedirectUri(),
        });
      }
    }

    axiosRequestConfig.headers.Authorization = `Bearer ${this.keycloak.token}`;
    return axiosRequestConfig;
  }

  handleInterceptorReject(error: unknown) {
    return Promise.reject(error);
  }

  /**
   * Secures an Axios client by attaching the current Keycloak access token
   * to every request as an Authorization header.
   *
   * Automatically refreshes the token if it's close to expiration.
   *
   * @param axiosClient - An Axios instance to secure.
   */
  secureAxiosClient(axiosClient: AxiosInstance): void {
    axiosClient.interceptors.request.use(this.handleInterceptorFulfilled, this.handleInterceptorReject);
  }
}
